import { animate, AnimationPlaybackControls, MotionValue, useMotionValue } from "motion/react";
import { useCallback, useRef, useState } from "react";

interface UseHoldToProgressOptions {
  /** Duration in milliseconds for the progress to complete */
  msTime: number;
  /** Callback when progress changes (0 to 1) */
  onProgress?: (progress: number) => void;
  /** Callback when progress completes (reaches 1) */
  onComplete?: () => void;
  /** Animation duration for resetting progress back to 0 */
  resetAnimationMs?: number;
}

interface UseHoldToProgressReturn {
  /** Current progress value (0 to 1) - MotionValue for smooth animations */
  progress: MotionValue<number>;
  /** Whether the user is currently holding */
  isHolding: boolean;
  /** Event handlers for the progress spinner */
  eventHandlers: {
    onMouseDown: () => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
    onTouchStart: () => void;
    onTouchEnd: () => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    onKeyUp: (e: React.KeyboardEvent) => void;
  };
}

export function useHoldToProgress({
  msTime,
  onProgress,
  onComplete,
  resetAnimationMs = 200,
}: UseHoldToProgressOptions): UseHoldToProgressReturn {
  const [isHolding, setIsHolding] = useState(false);

  // Use motion values instead of state
  const progress = useMotionValue(0);

  const animationRef = useRef<AnimationPlaybackControls | null>(null);

  const stopProgress = useCallback(() => {
    setIsHolding(false);

    if (animationRef.current) {
      animationRef.current.stop();
      animationRef.current = null;
    }
  }, []);

  const startProgress = useCallback(() => {
    if (isHolding) return;

    setIsHolding(true);

    const animation = animate(progress, 1, {
      duration: msTime / 1000,
      ease: "linear",
      onUpdate: (value) => {
        onProgress?.(value);
      },
      onComplete: () => {
        onComplete?.();
        stopProgress();
      },
    });

    animationRef.current = animation;
  }, [isHolding, progress, msTime, onProgress, onComplete, stopProgress]);

  const resetProgress = useCallback(() => {
    if (animationRef.current) {
      animationRef.current.stop();
      animationRef.current = null;
    }

    // Use motion's animate function for smooth reset animation
    const resetAnimation = animate(progress, 0, {
      duration: resetAnimationMs / 1000, // Convert to seconds
      ease: "easeOut",
      onUpdate: (value) => {
        onProgress?.(value);
      },
      onComplete: () => {
        onProgress?.(0);
      },
    });

    animationRef.current = resetAnimation;
  }, [progress, resetAnimationMs, onProgress]);

  const endProgress = useCallback(() => {
    stopProgress();
    resetProgress();
  }, [stopProgress, resetProgress]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        startProgress();
      }
    },
    [startProgress],
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        endProgress();
      }
    },
    [endProgress],
  );

  return {
    progress,
    isHolding,
    eventHandlers: {
      onMouseDown: startProgress,
      onMouseUp: endProgress,
      onMouseLeave: endProgress,
      onTouchStart: startProgress,
      onTouchEnd: endProgress,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
    },
  };
}
